import React, {createContext, useEffect, useReducer} from "react";
import SplashScreen from "src/components/SplashScreen";
import {HOSTS_CONFIG, REDUX_ACTIONS, USER_ROLES} from "src/constants";
import {authenticateUser, authenticateWithToken, logoutUser, refreshAuthenticationToken} from "src/api/auth";
import {changeLoggedUserPassword, getLoggedUserProfile} from "src/api/profile";
import {restoreSettings, storeSettings} from "src/contexts/SettingsContext";
import {getI18n} from "react-i18next";

const initialAuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null
};

const reducer = (state, action) => {
    switch (action.type) {
        case REDUX_ACTIONS.INITIALIZE: {
            const {isAuthenticated, user} = action.payload;
            return {
                ...state,
                isAuthenticated,
                isInitialized: true,
                user
            };
        }
        case REDUX_ACTIONS.LOGIN: {
            const {user} = action.payload;
            return {
                ...state,
                isAuthenticated: true,
                user
            };
        }
        case REDUX_ACTIONS.LOGOUT: {
            return {
                ...state,
                isAuthenticated: false,
                user: null
            };
        }
        case REDUX_ACTIONS.UPDATE_USER: {
            return {
                ...state,
                user: {...state.user, ...action.payload}
            };
        }
        default: {
            return {...state};
        }
    }
};

const AuthContext = createContext({
    ...initialAuthState,
    login: () => Promise.resolve(),
    sso: () => Promise.resolve(),
    logout: () => {
    },
    changePassword: () => Promise.resolve(),
    updateUser: () => {},
    hasAdminRole: () => false,
    hasUserRole: () => false
});

export const AuthProvider = ({children}) => {
    const [state, dispatch] = useReducer(reducer, initialAuthState);

    const hasAdminRole = () => {
        return state.user ? state.user.roles[0] === USER_ROLES.ADMIN.role : false;
    };

    const hasUserRole = () => {
        return state.user ? state.user.roles[0] === USER_ROLES.USER.role : false;
    };

    const login = async (username, password) => {
        await authenticateUser(username, password);
        const user = await getUserProfile();

        dispatch({
            type: REDUX_ACTIONS.LOGIN,
            payload: {
                user
            }
        });
    };

    const sso = async token => {
        await logoutUser();
        await authenticateWithToken(token);
        const user = await getUserProfile();

        dispatch({
            type: REDUX_ACTIONS.LOGIN,
            payload: {
                user
            }
        });
    };

    const logout = async () => {
        await logoutUser();
        dispatch({type: REDUX_ACTIONS.LOGOUT});
    };

    const changePassword = async (oldPassword, password) => {
        await changeLoggedUserPassword(oldPassword, password);
        const user = await getUserProfile();

        dispatch({
            type: REDUX_ACTIONS.INITIALIZE,
            payload: {
                isAuthenticated: true,
                user
            }
        });
    };

    const updateUser = (updates) => {
        dispatch({
            type: REDUX_ACTIONS.UPDATE_USER,
            payload: updates
        });
    };

    const refreshToken = async () => {
        await refreshAuthenticationToken();
    };

    const getUserProfile = async () => {
        const response = await getLoggedUserProfile();
        const user = {
            ...response.data,
            avatar: "/static/images/avatars/avatar.png"
        };
        if (user.language) {
            const hostConfig = HOSTS_CONFIG[window.location.hostname];
            const supportedLangs = (hostConfig && hostConfig.supportedLanguages) || [];
            if (supportedLangs.includes(user.language)) {
                const currentSettings = restoreSettings() || {};
                storeSettings({...currentSettings, language: user.language});
                getI18n().changeLanguage(user.language);
            }
        }
        return user;
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                await refreshToken();
                const user = await getUserProfile();

                dispatch({
                    type: REDUX_ACTIONS.INITIALIZE,
                    payload: {
                        isAuthenticated: true,
                        user
                    }
                });
            } catch (err) {
                dispatch({
                    type: REDUX_ACTIONS.INITIALIZE,
                    payload: {
                        isAuthenticated: false,
                        user: null
                    }
                });
            }
        };

        initialize();
    }, []);

    if (!state.isInitialized) {
        return <SplashScreen/>;
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                sso,
                logout,
                changePassword,
                updateUser,
                hasUserRole,
                hasAdminRole
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
